import { Component, OnInit, ViewChild, inject, DestroyRef } from '@angular/core';
import { Router } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreadcrumbItem } from 'src/app/models/breadcrumb/breadcrumb-item';
import { PermissionService } from 'src/app/services/api/permission.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { AddGroupComponent } from './add-group/add-group.component';
import { AssignRoleComponent } from './assign-role/assign-role.component';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { RouterSegments } from 'src/app/models/router-segments';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { PermissionValues } from 'src/app/models/permission-values';
import { PermissionKeys } from 'src/app/models/principal/permission-keys';
import { PermissionsService } from 'src/app/services/util/permissions.service';
import { Permissions } from 'src/app/models/principal/permission';

const REQUIRED_PERMISSIONS = [
  {
    key: PermissionKeys.GROUP_READ,
    action: Permissions.GROUP_READ,
  },
  {
    key: PermissionKeys.GROUP_CREATE,
    action: Permissions.GROUP_CREATE,
  },
  {
    key: PermissionKeys.GROUP_UPDATE,
    action: Permissions.GROUP_UPDATE,
  },
  {
    key: PermissionKeys.ROLE_ASSIGN_READ,
    action: Permissions.ROLE_ASSIGN_READ,
  },
  {
    key: PermissionKeys.ROLE_ASSIGN_CREATE,
    action: Permissions.ROLE_ASSIGN_CREATE,
  },
  {
    key: PermissionKeys.ROLE_ASSIGN_UPDATE,
    action: Permissions.ROLE_ASSIGN_UPDATE,
  }
];

@Component({
    selector: 'app-groups',
    templateUrl: './groups.component.html',
    styleUrls: ['./groups.component.scss'],
    standalone: false
})
export class GroupsComponent  implements OnInit {
  @ViewChild(MatSort, {static: true}) sort;
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  private destroyRef = inject(DestroyRef);
  RouterSegments = RouterSegments;
  breadcrumbItems: BreadcrumbItem[];
  isLoading = true;
  data = [];
  roleAssignments: any;
  dataSource: any;
  pageIndex = 0;
  paginationSize = 10;
  showPagination = true;
  displayedColumns: string[] = ['name', 'description', 'assigned', 'delete', 'assignRole'];
  showLoader = false;
  PermissionKeys = PermissionKeys;
  permissionsMap: { [key: string]: boolean; };

  constructor(private permissionsService: PermissionService, private toastrService: CustomToastrService,
    private authService: AuthService, private router: Router, private sidenavService: SidenavService,
    private dialog: MatDialog,  private permissionService: PermissionsService) {
    
  }

  ngOnInit(): void {
    this.permissionsMap = this.permissionService.resolveAPIPermissions(REQUIRED_PERMISSIONS);
    if (!this.permissionsMap[PermissionKeys.GROUP_UPDATE]) {
      this.displayedColumns = this.displayedColumns.filter(col => col !== 'delete');
    }
    if (!this.permissionsMap[PermissionKeys.GROUP_UPDATE] && (!this.permissionsMap[PermissionKeys.ROLE_ASSIGN_CREATE] || !this.permissionsMap[PermissionKeys.ROLE_ASSIGN_UPDATE])) {
      this.displayedColumns = this.displayedColumns.filter(col => col !== 'assignRole');
    }
    if (!this.permissionsMap[PermissionKeys.GROUP_READ]) {
      this.router.navigate(['/', this.authService.principal.provider, 'permission-unauthorized'], {skipLocationChange: true});
    }
    this.sidenavService.selectedMenu = 'LEFT_SIDE_NAV.GROUPS';
    this.breadcrumbItems = [];
    this.breadcrumbItems.push({icon: 'home'}, {label: translate('LEFT_SIDE_NAV.ACCESS_MANAGEMENT')}, 
      {label: translate('LEFT_SIDE_NAV.GROUPS'), url: ['groups']});
    this.getGroups();
  }

  getGroups(): void {
    this.isLoading = true;
    this.permissionsService.getGroups().pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(data => {
      if (data.total > 10) {
        const payload = {
          size: data.total
        };
        this.permissionsService.getGroups(payload).pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(groups => {
          this.data = groups.data;
          this.data.forEach(group => group.assigned = 0);
          this.loadData(this.data);
          this.isLoading = false;
          this.getRoleAssignments();
        },
        (error) => {
        setTimeout(() => {
		  const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
        });
      } else {
        this.data = data.data;
        this.data.forEach(group => group.assigned = 0);
        this.loadData(this.data);
        this.isLoading = false;
        this.getRoleAssignments();
      }
    }, 
    (error) => {
      this.isLoading = false;
        setTimeout(() => {
		  const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
    });
  }

  getRoleAssignments(): void {
    this.permissionsService.getRoleAssignments().pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(data => {
      const payload = {
        size: data.total
      };
      this.permissionsService.getRoleAssignments(payload).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(roleAssignments => {
        this.roleAssignments = roleAssignments.data;
        this.roleAssignments.forEach(role => {
          if (this.data.find(item => item.name === role?.principal?.split('/')[2])) {
            const assigned = this.data.find(item => item.name === role?.principal?.split('/')[2]).assigned;
            this.data.find(item => item.name === role?.principal?.split('/')[2]).assigned = assigned + 1;
          }
        })
      },
      (error) => {
        setTimeout(() => {
		  const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
      });
    },
    (error) => {
        setTimeout(() => {
		  const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
    });
  }

  loadData(data?): void {
    this.dataSource = new MatTableDataSource(data);
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  sortData(event): void {
    this.sort = event;
    this.showPagination = false;
    setTimeout(() => {
      this.showPagination = true;
    }, 10);
    this.pageIndex = 0;
    this.data = this.sortInstanceData(event);
    this.dataSource = new MatTableDataSource(this.data?.slice(this.pageIndex, this.paginationSize));
  }

  sortInstanceData(event): any {
    return this.data?.sort((a, b) => {
      let comparatorResult = 0;
      switch (event.active) {
        case 'name':
          comparatorResult = a?.name && b?.name ? a?.name?.localeCompare(b?.name) : !a?.name ? -1 : 1;
          break;
        case 'description':
          comparatorResult = a?.description && b?.description ? a?.description?.localeCompare(b?.description) : !a?.description ? -1 : 1;
          break;
        case 'assigned':
          comparatorResult = a?.assigned && b?.assigned ? (a?.assigned - b?.assigned) : !a?.assigned ? -1 : 1;
          break;
        default:
          comparatorResult = a?.name && b?.name ? a?.name?.localeCompare(b?.name) : !a?.name ? -1 : 1;
          break;
      }
      return comparatorResult * (event.direction === 'asc' ? 1 : -1);
     });
  }

  pageChanged(event): any {
    if (event.length) {
      this.pageIndex = event?.pageIndex;
      this.paginationSize = event?.pageSize;
      const firstIndex = this.pageIndex * this.paginationSize;
      const data = this.data.slice(firstIndex, firstIndex + this.paginationSize);
      this.dataSource = new MatTableDataSource(data);
    }
  }

  addGroup(): void {
    this.sidenavService.openInRightSidenav(AddGroupComponent)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((formData) => {
      if (formData) {
        this.getGroups();
        this.getAssignedPermissions();
      }
    });
  }

  deleteGroup(group): void {
    if (group.assigned > 0) {
      this.dialog.open(ErrorDialogComponent, {
        minHeight: '342px',
        width: '630px',
        panelClass: 'flex-dialog',
        data: {
          title: translate('GROUPS.DELETE_NOT_ALLOWED'),
          description: translate('GROUPS.DELETE_ROLE_ASSIGNED', {
          groupName: group.name}),
          hideReportButton: true,
        },
      });
    }
    else {
    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'default-dialog-wrapper',
      width: '566px',
      autoFocus: false,
      data: {
        title: translate('GROUPS.DELETE_GROUP'),
        message: translate('GROUPS.DELETE_GROUP_MESSAGE'),
        confirmButtonText: translate('BUTTONS.CONFIRM')
      }
    })
    .afterClosed()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(confirm => {
      if (confirm) {
        this.showLoader = true;
        this.permissionsService.deleteGroup(group?.name)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(data => {
          if (data) {
            setTimeout(() => {
                const message = translate('GROUPS.GROUP_DELETED');
                const title = translate('GROUPS.GROUPS');
                this.toastrService.successMessage(message, title);
            }, 500);
            this.getGroups();
            this.getAssignedPermissions();
            this.showLoader = false;
          }
        },
        (error) => {
          this.showLoader = false;
          const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
          this.dialog.open(ErrorDialogComponent, {
            minHeight: '342px',
            width: '630px',
            panelClass: 'flex-dialog',
            data: {
              title:  translate('GROUPS.GROUP_DELETION_FAILED'),
              description: errorMessage,
              hideReportButton: true
            }
          });
        });
      }
    });
  }
  }

  assignRole(group): void {
    if (!this.roleAssignments) {
      return;
    }
    const data = {
      name: group?.name,
      description: group?.description,
      roleAssignments: this.roleAssignments
    };
    this.sidenavService.openInRightSidenav(AssignRoleComponent, data)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((formData) => {
        if (!formData) {
          return;
        } else {
          this.getGroups();
          this.getAssignedPermissions();
        }
      });
  }

  groupNameClick(group): void {
    this.router.navigate(['/', this.authService.principal.provider, this.RouterSegments.GROUPS, group?.name]);
  }

  getAssignedPermissions(): void {
    let scope = '/global';
    this.authService.scope = scope;
    this.permissionsService.getAuthAssignedPermissions(scope)
    .pipe().subscribe(response => {
      this.authService.assignedPermissions = response.permissions;
    },
    (error) => {
        setTimeout(() => {
		  const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
    });
  }

}
